import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useTheme } from "../contexts/ThemeContext";
import GlobalAudioPlayer from "./GlobalAudioPlayer";
import CraftIcon from "./CraftIcon";
import CraftModeDialog from "./CraftModeDialog";
import { useToast } from "../contexts/ToastContext";
import api from "../api";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const aboutPaths = ["/why-loore", "/vision", "/how-to"];

function NavBar({ onNewEntryClick }) {
  const { user, loading: userLoading, setUser } = useUser();
  const { theme, toggleTheme } = useTheme();
  const { addToast } = useToast();
  const [craftDialogOpen, setCraftDialogOpen] = useState(false);
  // True from "Turn on" until the one-shot glow on the craft items ends.
  const [craftGlow, setCraftGlow] = useState(false);
  const location = useLocation();
  const [aboutOpen, setAboutOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const aboutRef = useRef(null);
  const overflowRef = useRef(null);

  // Craft mode state — read from user object or localStorage fallback
  const [craftMode, setCraftMode] = useState(() => {
    if (user && user.craft_mode !== undefined) return user.craft_mode;
    return localStorage.getItem('loore_craft_mode') === 'true';
  });

  // Sync craft mode when user loads
  useEffect(() => {
    if (user && user.craft_mode !== undefined) {
      setCraftMode(user.craft_mode);
    }
  }, [user]);

  const setCraftModeValue = async (newValue) => {
    setCraftMode(newValue);
    localStorage.setItem('loore_craft_mode', String(newValue));

    // Try to persist to backend if the user has craft_mode field
    if (user) {
      try {
        const response = await api.put('/dashboard/user', { craft_mode: newValue });
        if (response.data.user) {
          setUser(response.data.user);
        }
      } catch (e) {
        // Silently fall back to localStorage — backend may not support craft_mode yet
      }
    }
  };

  // Turning craft mode ON goes through a confirmation (CraftModeDialog);
  // turning it OFF is a plain flip.
  const handleCraftToggleClick = () => {
    if (craftMode) {
      setCraftModeValue(false);
      addToast('Craft mode off.');
      return;
    }
    setOverflowOpen(false);
    setCraftDialogOpen(true);
  };

  const confirmCraftMode = () => {
    setCraftDialogOpen(false);
    setCraftModeValue(true);
    // Reopen the menu the user came from: the three new items and the
    // flipped toggle are the immediate, visible result of the choice.
    setOverflowOpen(true);
    setCraftGlow(true);
    addToast('Craft mode on. Its controls carry the sliders icon.', 5000);
  };

  // When "Write" is clicked:
  const handleWriteClick = (e) => {
    e.preventDefault();
    setOverflowOpen(false);
    if (!user) {
      const returnUrl = encodeURIComponent(location.pathname + location.search);
      window.location.href = `/login?returnUrl=${returnUrl}`;
    } else {
      onNewEntryClick();
    }
  };

  const currentPath = location.pathname;
  const isAboutPage = aboutPaths.includes(currentPath);

  // Close dropdowns on click outside
  useEffect(() => {
    if (!aboutOpen && !overflowOpen) return;
    const handleClickOutside = (e) => {
      if (aboutOpen && aboutRef.current && !aboutRef.current.contains(e.target)) {
        setAboutOpen(false);
      }
      if (overflowOpen && overflowRef.current && !overflowRef.current.contains(e.target)) {
        setOverflowOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [aboutOpen, overflowOpen]);

  // Close dropdowns on navigation
  useEffect(() => {
    setAboutOpen(false);
    setOverflowOpen(false);
  }, [currentPath]);

  const linkStyle = (path) => ({
    color: currentPath === path ? 'var(--accent)' : 'var(--text-muted)',
    textDecoration: "none",
    fontFamily: "var(--sans)",
    fontWeight: 300,
    fontSize: "0.85rem",
    letterSpacing: "0.02em",
    transition: "color 0.3s ease",
  });

  const dropdownStyle = {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: "12px",
    background: "var(--bg-card)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    padding: "6px 0",
    minWidth: "220px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    zIndex: 1001,
  };

  // Icon + text row inside a menu item; matches the theme toggle's layout.
  const craftItemLabelStyle = { display: "flex", alignItems: "center", gap: "8px" };

  const dropdownItemStyle = {
    display: "block",
    padding: "10px 16px",
    fontFamily: "var(--sans)",
    fontWeight: 300,
    fontSize: "0.85rem",
    color: "var(--text-muted)",
    textDecoration: "none",
    cursor: "pointer",
    background: "none",
    border: "none",
    width: "100%",
    textAlign: "left",
    transition: "color 0.2s ease",
  };

  // The craft-only items sit in a wrapper with a 4px side margin (room for
  // the one-shot glow inside the card); 4px less padding keeps their icons
  // flush with the toggle's.
  const craftItemStyle = { ...dropdownItemStyle, padding: "10px 12px" };

  return (
    <>
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: "56px",
        backgroundColor: "var(--bg-surface)",
        borderBottom: "1px solid var(--border)",
        display: "flex",
        alignItems: "center",
        zIndex: 1000,
        padding: "0 clamp(14px, 4vw, 24px)",
      }}
    >
      {/* .nav-brand / .nav-links: index.css shrinks the wordmark and the
          link gap up to 412px so the kebab (logged in) and Login (logged
          out) stay on 360–400px phones (#284). */}
      <Link
        to="/"
        className="nav-brand"
        style={{
          color: "var(--text-secondary)",
          textDecoration: "none",
          fontFamily: "var(--serif)",
          fontWeight: 300,
          fontSize: "1.15rem",
          textTransform: "uppercase",
          letterSpacing: "0.3em",
          marginRight: "auto",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        <img
          src="/loore-logo-transparent.svg"
          alt=""
          style={{ height: "22px", width: "auto", opacity: 0.7 }}
        />
        Loore
      </Link>

      {/* The links tighten up on narrow screens so the group keeps a
          clear gap from the logo instead of running into it. */}
      <div
        className="nav-links"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "clamp(0.55rem, 2vw, 1.8rem)",
          marginLeft: "clamp(1.1rem, 5vw, 2.5rem)",
        }}
      >
        {currentPath !== '/voice' && <GlobalAudioPlayer />}

        {/* About dropdown — logged-out visitors only. For logged-in users
            the three pages live in the overflow menu below. Not while the
            session is still loading: user is null then too, and About
            flashed in the bar on every reload before the ⋮ menu took
            its place (alpha report 2026-09-05). */}
        {!user && !userLoading && (
        <div ref={aboutRef} style={{ position: "relative" }}>
          <button
            onClick={() => setAboutOpen(!aboutOpen)}
            style={{
              color: isAboutPage ? 'var(--accent)' : 'var(--text-muted)',
              textDecoration: "none",
              fontFamily: "var(--sans)",
              fontWeight: 300,
              fontSize: "0.85rem",
              letterSpacing: "0.02em",
              transition: "color 0.3s ease",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              display: "flex",
              alignItems: "center",
              gap: "3px",
            }}
          >
            About
            <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" style={{ opacity: 0.6 }}>
              <path d="M1 2.5L4 5.5L7 2.5" stroke="currentColor" strokeWidth="1.2" fill="none" />
            </svg>
          </button>
          {aboutOpen && (
            <div style={{ ...dropdownStyle, left: "50%", right: "auto", transform: "translateX(-50%)" }}>
              <Link to="/why-loore" style={{ ...dropdownItemStyle, color: currentPath === "/why-loore" ? "var(--accent)" : "var(--text-muted)" }}>
                Why Loore
              </Link>
              <Link to="/vision" style={{ ...dropdownItemStyle, color: currentPath === "/vision" ? "var(--accent)" : "var(--text-muted)" }}>
                Vision
              </Link>
              <Link to="/how-to" style={{ ...dropdownItemStyle, color: currentPath === "/how-to" ? "var(--accent)" : "var(--text-muted)" }}>
                How To
              </Link>
            </div>
          )}
        </div>
        )}

        {/* Home — the home page, same destination as the logo. The logo
            alone wasn't reading as a link, so the way back gets a named
            button too. Named "Home", not "Reflect" (#436, Peter
            2026-10-09): the home page now has a Reflect card of its own. */}
        {(!user || user.approved) && (
          <Link
            to="/"
            style={{
              ...linkStyle("/"),
              color:
                currentPath === "/" || currentPath === "/landing"
                  ? "var(--accent)"
                  : "var(--text-muted)",
            }}
          >
            Home
          </Link>
        )}

        {/* Artifacts — plain link into the documents workspace. It lands on
            Profile, where the ArtifactsNav bubble row cross-links Todo and
            every other artifact (one click each). Logged-out visitors see
            it too; it routes to login. */}
        {(!user || user.approved) && (
          <Link
            to="/profile"
            style={{
              ...linkStyle("/profile"),
              color:
                currentPath === "/profile" ||
                currentPath === "/todo" ||
                currentPath.startsWith("/artifacts")
                  ? "var(--accent)"
                  : "var(--text-muted)",
            }}
          >
            Artifacts
          </Link>
        )}

        {(!user || user.approved) && (
          <Link to="/log" style={linkStyle("/log")}>
            Log
          </Link>
        )}

        {/* Commons — the Log's public sibling (#228). Creating shares
            lives on the Home page as the third mode; the nav holds the
            two records: Log (private) and Commons (public). Dark behind
            SHARE_V1 via the dashboard payload flag. Shipped dark, so
            logged-out visitors (who can't read the flag) don't see it. */}
        {user && user.approved && user.share_v1_enabled && (
          <Link to="/commons" style={linkStyle("/commons")}>
            Commons
          </Link>
        )}

        {!user && (
          <Link
            to="/login?returnUrl=%2F"
            style={linkStyle("/login")}
          >
            Login
          </Link>
        )}

        {/* Three-dot overflow menu */}
        {user && (
          <div ref={overflowRef} style={{ position: "relative" }}>
            <button
              onClick={() => setOverflowOpen(!overflowOpen)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "3px",
                opacity: 0.5,
                transition: "opacity 0.2s ease",
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
            >
              <span style={{ width: "3px", height: "3px", borderRadius: "50%", background: "var(--text-muted)" }} />
              <span style={{ width: "3px", height: "3px", borderRadius: "50%", background: "var(--text-muted)" }} />
              <span style={{ width: "3px", height: "3px", borderRadius: "50%", background: "var(--text-muted)" }} />
            </button>
            {overflowOpen && (
              <div style={dropdownStyle}>
                {user.approved && (
                  <>
                    <Link to="/import" onClick={() => setOverflowOpen(false)} style={dropdownItemStyle}>
                      Import data
                    </Link>

                    {/* Bottom section */}
                    <div style={{ borderTop: "1px solid var(--border)", margin: "4px 0" }} />

                    {user.is_admin && (
                      <Link to="/admin" onClick={() => setOverflowOpen(false)} style={dropdownItemStyle}>
                        Admin
                      </Link>
                    )}

                    <Link to="/account" onClick={() => setOverflowOpen(false)} style={dropdownItemStyle}>
                      Account
                    </Link>

                    {/* The user's own public page (#228) — what visitors
                        see at /@<username>. */}
                    {user.share_v1_enabled && user.username && (
                      <Link
                        to={`/@${user.username}`}
                        onClick={() => setOverflowOpen(false)}
                        style={dropdownItemStyle}
                      >
                        My public page
                      </Link>
                    )}
                    {/* Theme toggle */}
                    <button
                      onClick={toggleTheme}
                      style={{ ...dropdownItemStyle, display: "flex", justifyContent: "space-between", alignItems: "center" }}
                      title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {theme === "light" ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="4" />
                            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                          </svg>
                        )}
                        <span>Light mode</span>
                      </span>
                      <div style={{
                        width: "32px",
                        height: "18px",
                        borderRadius: "9px",
                        background: theme === "light" ? "var(--accent)" : "var(--border)",
                        position: "relative",
                        transition: "background 0.2s ease",
                      }}>
                        <div style={{
                          width: "14px",
                          height: "14px",
                          borderRadius: "50%",
                          background: "var(--text-primary)",
                          position: "absolute",
                          top: "2px",
                          left: theme === "light" ? "16px" : "2px",
                          transition: "left 0.2s ease",
                        }} />
                      </div>
                    </button>

                    {/* Craft mode toggle. With the mode on, a divider above it
                        groups the toggle with the craft-only items below. */}
                    {craftMode && (
                      <div style={{ borderTop: "1px solid var(--border)", margin: "4px 0" }} />
                    )}
                    <button
                      onClick={handleCraftToggleClick}
                      title="Shows extra controls: privacy & AI usage per entry, auto-generate toggle, model picker, prompt editing, export."
                      style={{ ...dropdownItemStyle, display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                      <span style={craftItemLabelStyle}><CraftIcon />Craft mode</span>
                      <div style={{
                        width: "32px",
                        height: "18px",
                        borderRadius: "9px",
                        background: craftMode ? "var(--accent)" : "var(--border)",
                        position: "relative",
                        transition: "background 0.2s ease",
                      }}>
                        <div style={{
                          width: "14px",
                          height: "14px",
                          borderRadius: "50%",
                          background: "var(--text-primary)",
                          position: "absolute",
                          top: "2px",
                          left: craftMode ? "16px" : "2px",
                          transition: "left 0.2s ease",
                        }} />
                      </div>
                    </button>

                    {/* Craft-only items, right under the toggle so switching
                        off only collapses what sits below it. Each carries the
                        toggle's icon, so a user who turned craft mode on and
                        forgot can tie them back to the switch (alpha feedback 2026-09-05). */}
                    {craftMode && (
                      <>
                        <div
                          className={craftGlow ? "craft-glow" : undefined}
                          onAnimationEnd={() => setCraftGlow(false)}
                          style={{ margin: "0 4px" }}
                        >
                        <button onClick={handleWriteClick} style={craftItemStyle}>
                          <span style={craftItemLabelStyle}><CraftIcon />Write new entry</span>
                        </button>
                        <button onClick={() => {
                          setOverflowOpen(false);
                          api.get("/export/threads", { responseType: "blob" })
                            .then((res) => {
                              const url = window.URL.createObjectURL(new Blob([res.data]));
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `loore-export-${new Date().toISOString().slice(0, 10)}.txt`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            })
                            .catch((err) => console.error("Export failed:", err));
                        }} style={craftItemStyle}>
                          <span style={craftItemLabelStyle}><CraftIcon />Export data</span>
                        </button>
                        <Link to="/prompts" onClick={() => setOverflowOpen(false)} style={craftItemStyle}>
                          <span style={craftItemLabelStyle}><CraftIcon />Prompts</span>
                        </Link>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* About pages — moved out of the top nav for logged-in
                    users; the public dropdown remains for visitors. */}
                <div style={{ borderTop: "1px solid var(--border)", margin: "4px 0" }} />
                <div
                  style={{
                    padding: "6px 16px 4px",
                    fontFamily: "var(--sans)",
                    fontWeight: 300,
                    fontSize: "0.7rem",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                    opacity: 0.6,
                  }}
                >
                  About
                </div>
                <Link to="/why-loore" onClick={() => setOverflowOpen(false)} style={{ ...dropdownItemStyle, color: currentPath === "/why-loore" ? "var(--accent)" : "var(--text-muted)" }}>
                  Why Loore
                </Link>
                <Link to="/vision" onClick={() => setOverflowOpen(false)} style={{ ...dropdownItemStyle, color: currentPath === "/vision" ? "var(--accent)" : "var(--text-muted)" }}>
                  Vision
                </Link>
                <Link to="/how-to" onClick={() => setOverflowOpen(false)} style={{ ...dropdownItemStyle, color: currentPath === "/how-to" ? "var(--accent)" : "var(--text-muted)" }}>
                  How To
                </Link>

                <div style={{ borderTop: "1px solid var(--border)", margin: "4px 0" }} />
                <a href={`${backendUrl}/auth/logout`} style={dropdownItemStyle}>
                  Logout
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
    <CraftModeDialog
      open={craftDialogOpen}
      onClose={() => setCraftDialogOpen(false)}
      onConfirm={confirmCraftMode}
    />
    </>
  );
}

export default NavBar;
